import type { Currency, Formula } from "@inrev/common";
import type { FinancialStatementPeriodId, ViewMode } from "../types";

export type FinancialEditState =
	| { kind: "idle" }
	| {
			kind: "creating_row";
			parentRowId: string;
			labelDraft: string;
			valueDrafts: Record<FinancialStatementPeriodId, string>;
			valueErrors: Record<FinancialStatementPeriodId, string>;
			adjustmentDrafts: Record<FinancialStatementPeriodId, string>;
			adjustmentErrors: Record<FinancialStatementPeriodId, string>;
			formError?: string;
	  }
	| {
			kind: "editing_label";
			rowId: string;
			labelDraft: string;
	  };

export type FinancialField = "given" | "adjustment";

export type CellErrorKind = "parse" | "save";

// A per-cell error. `parse` errors block persistence and always carry the
// user's raw draft so the edit input rehydrates on re-focus. `save` errors
// come from a failed API persist; the draft is the value the user tried to
// save, so re-focusing rehydrates their attempt rather than the rolled-back
// value. Parse and save are mutually exclusive for a given cell + field.
export type CellError = { kind: CellErrorKind; message: string; draft: string };

export type FinancialState = {
	view: ViewMode;
	edit: FinancialEditState;
	cellErrors: Map<string, CellError>;
	rowError: string | null;
	showPpChange: boolean;
	hideEmptyRows: boolean;
};

export type FinancialAction<
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> =
	| { type: "set_view"; view: ViewMode }
	| {
			type: "set_given_override";
			rowId: string;
			periodId: TPeriodId;
			value: number | null;
			draft: string;
	  }
	| {
			type: "set_given_error";
			rowId: string;
			periodId: TPeriodId;
			message: string;
			draft: string;
	  }
	| {
			type: "set_adjustment_override";
			rowId: string;
			periodId: TPeriodId;
			value: Currency | null;
			formula?: Formula;
			draft: string;
	  }
	| {
			type: "set_adjustment_error";
			rowId: string;
			periodId: TPeriodId;
			message: string;
			draft: string;
	  }
	| {
			type: "start_create_row";
			parentRowId: string;
			labelDraft?: string;
			valueDrafts?: Record<FinancialStatementPeriodId, string>;
			adjustmentDrafts?: Record<FinancialStatementPeriodId, string>;
	  }
	| { type: "change_create_label"; label: string }
	| { type: "change_create_value"; periodId: TPeriodId; value: string }
	| { type: "set_create_value_error"; periodId: TPeriodId; message: string }
	| { type: "clear_create_value_error"; periodId: TPeriodId }
	| { type: "change_create_adjustment"; periodId: TPeriodId; value: string }
	| { type: "set_create_adjustment_error"; periodId: TPeriodId; message: string }
	| { type: "clear_create_adjustment_error"; periodId: TPeriodId }
	| { type: "set_create_form_error"; message: string }
	| { type: "cancel_create_row" }
	| { type: "start_rename_row"; rowId: string; labelDraft?: string }
	| { type: "change_rename_label"; label: string }
	| { type: "cancel_rename_row" }
	| {
			type: "set_save_error";
			rowId: string;
			periodId: TPeriodId;
			field: FinancialField;
			message: string;
			draft: string;
	  }
	| {
			type: "clear_save_error";
			rowId: string;
			periodId: TPeriodId;
			field: FinancialField;
	  }
	| { type: "set_row_error"; rowId: string }
	| { type: "clear_row_error"; rowId: string }
	| { type: "toggle_pp_change" }
	| { type: "toggle_hide_empty_rows" };

export const cellErrorKey = <TPeriodId extends FinancialStatementPeriodId>(
	rowId: string,
	periodId: TPeriodId,
	field: FinancialField,
): string => `${rowId}::${periodId}::${field}`;

export const initialFinancialState = (defaults: { view: ViewMode }): FinancialState => ({
	view: defaults.view,
	edit: { kind: "idle" },
	cellErrors: new Map(),
	rowError: null,
	showPpChange: false,
	hideEmptyRows: true,
});

// Re-committing a cell is a retry: drop whatever error (parse or save) was
// recorded for that cell + field. Returns a referentially-stable no-op when
// there is nothing to clear.
const clearCellError = <TPeriodId extends FinancialStatementPeriodId>(
	state: FinancialState,
	rowId: string,
	periodId: TPeriodId,
	field: FinancialField,
): FinancialState => {
	const key = cellErrorKey(rowId, periodId, field);
	if (!state.cellErrors.has(key)) return state;
	const cellErrors = new Map(state.cellErrors);
	cellErrors.delete(key);
	return { ...state, cellErrors };
};

export function financialReducer<TPeriodId extends FinancialStatementPeriodId>(
	state: FinancialState,
	action: FinancialAction<TPeriodId>,
): FinancialState {
	switch (action.type) {
		case "set_view":
			if (state.view === action.view) return state;
			return { ...state, view: action.view };

		case "set_given_override":
			return clearCellError(state, action.rowId, action.periodId, "given");

		case "set_given_error": {
			const cellErrors = new Map(state.cellErrors);
			cellErrors.set(cellErrorKey(action.rowId, action.periodId, "given"), {
				kind: "parse",
				message: action.message,
				draft: action.draft,
			});
			return { ...state, cellErrors };
		}

		case "set_adjustment_override":
			return clearCellError(state, action.rowId, action.periodId, "adjustment");

		case "set_adjustment_error": {
			const cellErrors = new Map(state.cellErrors);
			cellErrors.set(cellErrorKey(action.rowId, action.periodId, "adjustment"), {
				kind: "parse",
				message: action.message,
				draft: action.draft,
			});
			return { ...state, cellErrors };
		}

		case "start_create_row":
			return {
				...state,
				edit: {
					kind: "creating_row",
					parentRowId: action.parentRowId,
					labelDraft: action.labelDraft ?? "",
					valueDrafts: action.valueDrafts ?? {},
					valueErrors: {},
					adjustmentDrafts: action.adjustmentDrafts ?? {},
					adjustmentErrors: {},
				},
			};

		case "change_create_label":
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.labelDraft === action.label) return state;
			return { ...state, edit: { ...state.edit, labelDraft: action.label, formError: undefined } };

		case "change_create_value": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.valueDrafts[action.periodId] === action.value) return state;
			const { [action.periodId]: _cleared, ...remainingErrors } = state.edit.valueErrors;
			return {
				...state,
				edit: {
					...state.edit,
					valueDrafts: { ...state.edit.valueDrafts, [action.periodId]: action.value },
					valueErrors: remainingErrors,
					formError: undefined,
				},
			};
		}

		case "set_create_form_error": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.formError === action.message) return state;
			return { ...state, edit: { ...state.edit, formError: action.message } };
		}

		case "set_create_value_error": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.valueErrors[action.periodId] === action.message) return state;
			return {
				...state,
				edit: {
					...state.edit,
					valueErrors: { ...state.edit.valueErrors, [action.periodId]: action.message },
				},
			};
		}

		case "clear_create_value_error": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.valueErrors[action.periodId] === undefined) return state;
			const { [action.periodId]: _cleared, ...remainingErrors } = state.edit.valueErrors;
			return { ...state, edit: { ...state.edit, valueErrors: remainingErrors } };
		}

		case "change_create_adjustment": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.adjustmentDrafts[action.periodId] === action.value) return state;
			const { [action.periodId]: _cleared, ...remainingErrors } = state.edit.adjustmentErrors;
			return {
				...state,
				edit: {
					...state.edit,
					adjustmentDrafts: { ...state.edit.adjustmentDrafts, [action.periodId]: action.value },
					adjustmentErrors: remainingErrors,
					formError: undefined,
				},
			};
		}

		case "set_create_adjustment_error": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.adjustmentErrors[action.periodId] === action.message) return state;
			return {
				...state,
				edit: {
					...state.edit,
					adjustmentErrors: { ...state.edit.adjustmentErrors, [action.periodId]: action.message },
				},
			};
		}

		case "clear_create_adjustment_error": {
			if (state.edit.kind !== "creating_row") return state;
			if (state.edit.adjustmentErrors[action.periodId] === undefined) return state;
			const { [action.periodId]: _cleared, ...remainingErrors } = state.edit.adjustmentErrors;
			return { ...state, edit: { ...state.edit, adjustmentErrors: remainingErrors } };
		}

		case "cancel_create_row":
			if (state.edit.kind !== "creating_row") return state;
			return { ...state, edit: { kind: "idle" } };

		case "start_rename_row":
			return {
				...state,
				edit: {
					kind: "editing_label",
					rowId: action.rowId,
					labelDraft: action.labelDraft ?? "",
				},
			};

		case "change_rename_label":
			if (state.edit.kind !== "editing_label") return state;
			if (state.edit.labelDraft === action.label) return state;
			return { ...state, edit: { ...state.edit, labelDraft: action.label } };

		case "cancel_rename_row":
			if (state.edit.kind !== "editing_label") return state;
			return { ...state, edit: { kind: "idle" } };

		case "set_save_error": {
			const key = cellErrorKey(action.rowId, action.periodId, action.field);
			const existing = state.cellErrors.get(key);
			if (
				existing?.kind === "save" &&
				existing.message === action.message &&
				existing.draft === action.draft
			)
				return state;
			const cellErrors = new Map(state.cellErrors);
			cellErrors.set(key, { kind: "save", message: action.message, draft: action.draft });
			return { ...state, cellErrors };
		}

		case "clear_save_error": {
			const key = cellErrorKey(action.rowId, action.periodId, action.field);
			if (state.cellErrors.get(key)?.kind !== "save") return state;
			const cellErrors = new Map(state.cellErrors);
			cellErrors.delete(key);
			return { ...state, cellErrors };
		}

		case "set_row_error":
			if (state.rowError === action.rowId) return state;
			return { ...state, rowError: action.rowId };

		case "clear_row_error":
			if (state.rowError === null || state.rowError !== action.rowId) return state;
			return { ...state, rowError: null };

		case "toggle_pp_change":
			return { ...state, showPpChange: !state.showPpChange };

		case "toggle_hide_empty_rows":
			return { ...state, hideEmptyRows: !state.hideEmptyRows };
	}
}
